import {
  Box,
  Card,
  CardContent,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import InfoPopper from "../../../../../components/popper/InfoPopper";
import { ChartAndTableButton } from "../../../../../components/chart/ChartAndTableButton";
import { t } from "i18next";
import { EcoEfficiencyTable } from "./EcoEfficiencyTable";
import BarChartWithbaseline from "../../../../../components/chart/BarChartWithBaseline";
import { BarSeriesType, ChartsAxisContentProps } from "@mui/x-charts";
import { MakeOptional } from "@mui/x-charts/models/helpers";
import { GridColDef, GridColumnGroupingModel } from "@mui/x-data-grid";
import ChartCheckbox from "../../../../../components/chart/ChartCheckbox";
import { DashedLineIcon } from "../../../../../components/icon/DashedLineIcon";
import { MenuData } from "../../../../../components/input/CustomSelect";
import { useTranslation } from "react-i18next";
import NumberFormatterUtils from "../../../../../utils/numberFormatterUtils";
import { EcoEfficiencyDashboardContext } from "../../../../../contexts/EcoEfficiencyDashboardContext";

interface Props {
  header: string;
  info: string;
  series: MakeOptional<BarSeriesType, "type">[];
  labels: string[];
  year: number;
  rows: any[];
  columns: GridColDef<any>[];
  columnGroupingModel?: GridColumnGroupingModel;
  selectOption?: MenuData[];
  select?: string;
  setSelect?: (value: string) => void;
  customTooltip?: (props: ChartsAxisContentProps) => JSX.Element;
  pagination?: JSX.Element;
}

export type displayType = "table" | "chart";

const CustomEcoEfficiencyChart = ({
  header,
  info,
  series,
  labels,
  year,
  rows,
  columns,
  columnGroupingModel,
  selectOption,
  select,
  setSelect,
  customTooltip,
  pagination,
}: Props) => {
  const { selectedEcoEfficiency } = EcoEfficiencyDashboardContext();

  const { i18n } = useTranslation([], { nsMode: "fallback" });

  const [type, setType] = useState<displayType>("chart");

  const [filter, setFilter] = useState<string[]>([]);

  const toggleFilter = (key: string) => {
    setFilter((prev) => {
      const isSelected = prev.includes(key);
      return isSelected ? prev.filter((f) => f !== key) : [...prev, key];
    });
  };

  useEffect(() => {
    setFilter(["baseYear", ...series.map((s) => (typeof s.label === "string" ? s.label : ""))]);
  }, [series, labels]);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "8px",
        padding: "24px",
        ".MuiCardContent-root": {
          padding: "0px !important",
        },
        // marginX: "40px",
      }}
    >
      <CardContent>
        <Box display={"flex"} flexDirection={"column"} gap={"20px"}>
          <Box
            display={"flex"}
            flexDirection={"row"}
            alignItems={"center"}
            gap={"8px"}
          >
            <Typography variant="h3" flexGrow={1}>
              {header}
              <InfoPopper text={info} />
            </Typography>

            {setSelect && select && selectOption && (
              <Box
                display={"flex"}
                flexDirection={"row"}
                m={"0px 12px"}
                gap={"8px"}
              >
                <img src="/img/setting-5.svg" alt="setting" />
                <FormControl variant="standard">
                  <Select
                    sx={{ width: "200px" }}
                    value={select}
                    onChange={(event: SelectChangeEvent<string>) => {
                      if (event.target.value) setSelect(event.target.value);
                    }}
                  >
                    {selectOption?.map((f, index) => (
                      <MenuItem key={index} value={f.value}>
                        {i18n.language === "th" ? f!.nameTH : f!.nameEN}
                      </MenuItem>
                    )) ?? undefined}
                  </Select>
                </FormControl>
              </Box>
            )}

            <ChartAndTableButton setType={setType} type={type} />
          </Box>
          {/* Chart label */}
          <Box
            display={"flex"}
            justifyContent={"flex-end"}
            gap={"24px"}
            flexWrap={"wrap"}
            style={{ width: "100%" }}
          >
            {selectedEcoEfficiency && (
              <ChartCheckbox
                key={`baseYear`}
                text={`${t("Base-Year")} (${
                  selectedEcoEfficiency!.baseYear ?? ""
                })`}
                color={""}
                checked={filter.includes("baseYear")}
                onChange={(_, checked) => toggleFilter("baseYear")}
                icon={<DashedLineIcon />}
              />
            )}

            {series.map((e) => (
              <ChartCheckbox
                key={`${e.label}`}
                text={typeof e.label === "string" ? e.label : ""}
                color={e.color ?? ""}
                checked={filter.includes(`${e.label}`)}
                onChange={(_, checked) => toggleFilter(`${e.label}`)}
              />
            ))}
          </Box>

          <Box style={{ width: "100%" }} sx={{ overflowX: "auto" }}>
            {type === "table" ? (
              <EcoEfficiencyTable
                rows={rows}
                columns={columns}
                columnGroupingModel={columnGroupingModel}
              />
            ) : (
              <BarChartWithbaseline
                series={series.filter((s) => filter.includes(typeof s.label === "string" ? s.label : ""))}
                labels={labels}
                seriesLabel={t("Ratio")}
                trend={true}
                layout="vertical"
                baselineValue={1}
                showBaseline={filter.includes("baseYear")}
                baselineLabel="Target"
                left={120}
                mutipleHeight={25}
                total={100}
                preview={false}
                tooltip={
                  series.length > 0 && labels.length > 0
                    ? (props: ChartsAxisContentProps) =>
                        customTooltip
                          ? customTooltip(props)
                          : Tooltip(props, year, header)
                    : undefined
                }
              />
            )}
          </Box>

          {pagination}
        </Box>
      </CardContent>
    </Card>
  );
};

export default CustomEcoEfficiencyChart;

export const Tooltip = (
  props: ChartsAxisContentProps,
  year: number,
  header: string,
) => {
  return (
    <Card
      sx={{
        bgcolor: "var(--white)",
        borderRadius: "8px",
        padding: "20px",
        minWidth: "320px",
      }}
    >
      <CardContent
        sx={{ display: "flex", flexDirection: "column", gap: "12px" }}
      >
        <Box borderBottom={"1px solid var(--gray2)"}>
          <Typography variant="h3" color="secondary">
            {props.axisValue != null ? String(props.axisValue) : ""}
          </Typography>
        </Box>

        {/* <Box borderBottom={"1px solid var(--gray2)"}>
          <Typography variant="h3" color="secondary">
            {year}
          </Typography>
        </Box>  */}

        {props.series.map((s: any, index) => (
          <Box
            borderBottom={"1px solid var(--gray2)"}
            display={"flex"}
            flexDirection={"row"}
            key={index}
          >
            <Box
              key={s.color}
              display={"inline-flex"}
              gap={"8px"}
              alignItems={"center"}
              flex={1}
            >
              <Box
                width={"16px"}
                height={"16px"}
                bgcolor={s.color}
                borderRadius={"4px"}
              />
              <Typography variant="body1" color="secondary">
                {s.label}
              </Typography>
            </Box>

            <Box flex={1} textAlign={"right"}>
              {header === t("Eco-Efficiency-Performance-Over-Time") ||
              props.axisValue === t("Eco-Efficiency")
                ? `${NumberFormatterUtils.numberFormat(
                    s.data[props.dataIndex ?? 0],
                    4,
                  )}`
                : `${NumberFormatterUtils.numberFormat(
                    s.data[props.dataIndex ?? 0],
                  )}`}
            </Box>

            <Box flex={1}></Box>
          </Box>
        ))}
      </CardContent>
    </Card>
  );
};
